/**
 * BaselineChecklist — Guided "First 5 Lifts" card for new users.
 * Shows on the home screen when baseline is incomplete.
 * Each lift is tappable and routes to the record tab with that exercise pre-selected.
 */

import { View, Text, StyleSheet, TouchableOpacity, Alert, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { CheckCircle, Circle, ChevronRight, Info } from 'lucide-react-native';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, SPACE, CARD, SECTION_LABEL, TRACKING } from '@/constants/designTokens';
import type { BaselineStatus } from '@/lib/baselineGate';
import InfoSheet from '@/components/InfoSheet';
import Confetti from '@/components/Confetti';
import { INFO_CONTENT } from '@/lib/infoContent';
import { useState, useEffect } from 'react';

interface Props {
  status: BaselineStatus;
}

const LIFT_DISPLAY: Record<string, string> = {
  'bench-press': 'Bench Press',
  'back-squat': 'Back Squat',
  'deadlift': 'Deadlift',
  'weighted-pull-up': 'Pull-Ups',
  'dead-hang': 'Dead Hang',
};

const LIFT_HINTS: Record<string, string> = {
  'bench-press': 'Flat barbell or dumbbell bench press. Log weight × reps.',
  'back-squat': 'Barbell back squat to parallel or below. Log weight × reps.',
  'deadlift': 'Conventional or sumo deadlift from the floor. Log weight × reps.',
  'weighted-pull-up': 'Pull-ups with added weight. No external load? Log 0 extra weight — your bodyweight counts.',
  'dead-hang': 'Hang from a pull-up bar as long as you can. Just enter your time in seconds.',
};

export default function BaselineChecklist({ status }: Props) {
  const router = useRouter();
  const { theme } = useTheme();
  const { profile } = useAuth();
  const c = theme.colors;
  const done = status.liftsLoggedEver;
  const [showInfo, setShowInfo] = useState(false);
  const [showCore5Confetti, setShowCore5Confetti] = useState(false);

  // Core 5 Complete celebration
  useEffect(() => {
    if (status.isComplete && profile?.id) {
      AsyncStorage.getItem(`duel:core5_celebrated:${profile.id}`).then((val) => {
        if (!val) {
          AsyncStorage.setItem(`duel:core5_celebrated:${profile.id}`, '1').catch(() => {});
          setShowCore5Confetti(true);
          const msg = "🏆 Core 5 Complete! You're now ranked.";
          if (Platform.OS === 'web') alert(msg);
          else Alert.alert('Core 5 Complete!', msg);
          setTimeout(() => setShowCore5Confetti(false), 3000);
        }
      }).catch(() => {});
    }
  }, [status.isComplete, profile?.id]);

  if (status.isComplete) return showCore5Confetti ? <Confetti active /> : null;

  return (
    <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
      <View style={styles.header}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACE.xs }}>
          <Text style={[styles.label, { color: c.accent }]}>GET RANKED</Text>
          <TouchableOpacity onPress={() => setShowInfo(true)} style={{ padding: 2, opacity: 0.7 }} accessibilityRole="button" accessibilityLabel="Core 5 lifts info">
            <Info size={14} color={c.textSecondary} />
          </TouchableOpacity>
        </View>
        <Text style={[styles.title, { color: c.text }]}>LOG YOUR FIRST 5 LIFTS</Text>
        <Text style={[styles.subtitle, { color: c.textSecondary }]}>
          Complete all 5 to unlock your Strength Score and start competing.
        </Text>
      </View>

      {/* Progress bar */}
      <View style={styles.progressRow}>
        {status.lifts.map((lift, i) => (
          <View
            key={lift.lift}
            style={[
              styles.progressSegment,
              { backgroundColor: lift.status !== 'missing' ? c.accent : c.border },
            ]}
          />
        ))}
      </View>
      <Text style={[styles.progressText, { color: c.textSecondary }]}>
        {done}/5 complete
      </Text>

      {/* Lift checklist */}
      <View style={styles.lifts}>
        {status.lifts.map((lift) => {
          const logged = lift.status !== 'missing';
          const displayName = LIFT_DISPLAY[lift.lift] || lift.label;
          return (
            <TouchableOpacity
              key={lift.lift}
              style={[styles.liftRow, { borderColor: c.border, opacity: logged ? 0.5 : 1 }]}
              onPress={() => {
                if (!logged) {
                  router.push({ pathname: '/(tabs)/record', params: { exercise: displayName } } as any);
                }
              }}
              disabled={logged}
              activeOpacity={0.7}
            >
              {logged ? (
                <CheckCircle size={18} color={c.accent} />
              ) : (
                <Circle size={18} color={c.textTertiary} />
              )}
              <View style={styles.liftTextCol}>
                <Text style={[styles.liftName, { color: logged ? c.textSecondary : c.text }]}>
                  {displayName}
                </Text>
                {!logged && LIFT_HINTS[lift.lift] && (
                  <Text style={[styles.liftHint, { color: c.textTertiary }]}>
                    {LIFT_HINTS[lift.lift]}
                  </Text>
                )}
              </View>
              {!logged && <ChevronRight size={14} color={c.textTertiary} />}
            </TouchableOpacity>
          );
        })}
      </View>
      <InfoSheet
        visible={showInfo}
        onClose={() => setShowInfo(false)}
        title={INFO_CONTENT.baselineLifts.title}
        content={INFO_CONTENT.baselineLifts.content}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  card: { ...CARD.standard, padding: SPACE.lg, gap: SPACE.md },
  header: { gap: SPACE.xs },
  label: { ...SECTION_LABEL, fontFamily: TYPO.subheading.bold },
  title: { fontSize: FONT.lg, fontFamily: TYPO.heading.bold, textTransform: 'uppercase', letterSpacing: 1 },
  subtitle: { fontSize: FONT.sm, fontFamily: TYPO.body.regular, lineHeight: 20 },
  progressRow: { flexDirection: 'row', height: 4, gap: 2 },
  progressSegment: { flex: 1, borderRadius: RADIUS.xs },
  progressText: { fontSize: FONT.xs, fontFamily: TYPO.subheading.bold, textTransform: 'uppercase', letterSpacing: TRACKING.wide },
  lifts: { gap: SPACE.xs },
  liftRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACE.sm,
    paddingVertical: SPACE.sm,
    borderBottomWidth: 1,
  },
  liftTextCol: { flex: 1, gap: 2 },
  liftName: { fontSize: FONT.base, fontFamily: TYPO.body.semibold },
  liftHint: { fontSize: FONT.xs, fontFamily: TYPO.body.regular, lineHeight: 16 },
});
